import * as React from 'react';
import classnames from 'classnames';

import { FormField } from 'components/form';
import MixedSelectDropdown from 'components/inputs/mixed-select-dropdown/MixedSelectDropdown';
import InputToggles from 'components/inputs/input-toggles/InputToggles';

import type { FieldDescriptionShowParams } from 'modules/influencers/components/influencer-create-profile/InfluencerCreateProfile';

import { __t, applyTranslations } from 'utils/language';
import { isArrayRequired, maxLengthArray } from 'utils/validators';
import {
  PROFESSION_OPTIONS,
  SPECIALIZED_IN_OPTIONS
} from 'utils/influencers';

import type {
  Data,
  InfluencerProfileForm,
  InfluencerSpecializedIn,
  MixedSelectDropdownItem
} from 'types';

applyTranslations(require('locales/common/collabary-common.i18n.json'));
applyTranslations(require('./fields-about-work.i18n.json'));

type Props = {
  details: InfluencerProfileForm,
  specializedIn: InfluencerSpecializedIn,
  onDetailsChange: (details: InfluencerProfileForm) => void,
  onSpecializedInChange: (specializedInCustom: Array<string>) => void,
  onFieldDescriptionShow?: (params: FieldDescriptionShowParams) => void,
  onFieldDescriptionHide?: () => void
};

const COMMON_MSG_KEY = 'collabary_common';
const MSG_KEY = 'fields_about_work';

const SPECIALIZED_IN_ITEMS = SPECIALIZED_IN_OPTIONS.map(option => ({
  value: option,
  label: __t(`${COMMON_MSG_KEY}.specialized_in.${option}`)
}));

const SPECIALIZED_IN_MAX_LENGTH = 3;

class ProfileFormFieldsAboutWork extends React.PureComponent<Props> {
  componentWillUnmount() {
    const { onFieldDescriptionHide } = this.props;

    if (onFieldDescriptionHide) {
      onFieldDescriptionHide();
    }
  }

  getDropdownItems = (): Array<MixedSelectDropdownItem> => {
    const { details } = this.props;

    return [...PROFESSION_OPTIONS].map(value => ({
      identifier: value,
      isDisabled: false,
      isSelected: details.profession === value,
      label: __t(`${COMMON_MSG_KEY}.jobs.${value}`),
      value
    }));
  };

  handleFieldChanged = (event: React.SyntheticEvent<HTMLInputElement>): void => {
    const { details, onDetailsChange } = this.props;
    const target = event.currentTarget;
    const targetValue = target.value;
    const targetName = target.name;

    onDetailsChange({
      ...details,
      [targetName]: targetValue
    });
  };

  handleProfessionChanged = (
    updatedItems: Array<MixedSelectDropdownItem>,
    data?: Data | null,
    selectedItem?: Partial<MixedSelectDropdownItem> | null
  ): void => {
    const { details, onDetailsChange } = this.props;

    onDetailsChange({
      ...details,
      profession: selectedItem?.identifier || updatedItems[0].identifier
    });
  };

  handleSpecializedInChanged = (values: Array<string>): void => {
    const { onSpecializedInChange } = this.props;

    onSpecializedInChange(values);
  };

  getHandleForAreaMouseEnter = (areaName: 'profession' | 'specialization'): () => void => (
    (): void => {
      const { onFieldDescriptionShow } = this.props;
      let positionTop: number;
      let name: string;
      const width = 340;

      if (areaName === 'profession') {
        positionTop = 30;
        name = 'profession';
      } else {
        positionTop = 120;
        name = 'specializedIn';
      }

      if (onFieldDescriptionShow) {
        onFieldDescriptionShow({
          name,
          positionTop,
          width
        });
      }
    }
  );

  render() {
    const { details, specializedIn, onFieldDescriptionHide } = this.props;
    const hasStaticTooltips = !onFieldDescriptionHide;
    const specializedInCustom = specializedIn ? specializedIn.custom : [];
    const dropdownItems = this.getDropdownItems();

    return (
      <section className="profile-form-fields">
        <div className="profile-form-fields__tooltipable-area">
          <div className="profile-form-fields__form-field-width-wrapper">
            <FormField
              label={__t(`${MSG_KEY}.fields.profession`)}
              labelType="highlighted"
              tooltipMessage={
                hasStaticTooltips ? __t(`${MSG_KEY}.field_descriptions.profession`) : ''
              }
              direction="column"
              showRequiredMark={false}
              showValidationIcon={false}
            >
              <MixedSelectDropdown
                key={`profile-form-fields__profession-dropdown--${details.profession}`}
                name="profile-form-fields__profession-dropdown"
                items={dropdownItems}
                placeholder={__t(`${MSG_KEY}.fields.profession_placeholder`)}
                withSelectedInList
                className="profile-form-fields__profession-dropdown"
                onItemsToggle={this.handleProfessionChanged}
              />
            </FormField>
          </div>
        </div>
        <div
          className={classnames('profile-form-fields__tooltipable-area', {
            'profile-form-fields__tooltipable-area--with-padding-top': true
          })}
          onMouseEnter={this.getHandleForAreaMouseEnter('specialization')}
          onMouseLeave={onFieldDescriptionHide}
        >
          <div className="profile-form-fields__form-field-width-wrapper">
            <FormField
              label={__t(`${MSG_KEY}.fields.specialized_in`)}
              labelType="highlighted"
              tooltipMessage={
                hasStaticTooltips ? __t(`${MSG_KEY}.field_descriptions.specialized_in`) : ''
              }
              direction="column"
              showRequiredMark
              showValidationIcon={false}
              validators={[
                isArrayRequired(specializedInCustom),
                maxLengthArray(specializedInCustom, SPECIALIZED_IN_MAX_LENGTH)
              ]}
            >
              <InputToggles
                items={SPECIALIZED_IN_ITEMS}
                value={specializedInCustom || []}
                limit={SPECIALIZED_IN_MAX_LENGTH}
                onChange={this.handleSpecializedInChanged}
              />
            </FormField>
          </div>
        </div>
      </section>
    );
  }
}

export default ProfileFormFieldsAboutWork;
